import {
  lazyPipe,
  validateApiRequest,
  validateApiResponse,
  withApiErrorCapture,
} from '@musinsa/apis-common-api-zod4';
import { executeStandardRequest } from '@musinsa/packages-http';
import { z } from 'zod';
import { createApiResponseSchema } from '../../../utils';
import { partnerApiPath } from '../../apiPath';
import { partnerHttpClient } from '../../httpClient';

const UpdateSeedingCampaignSnsChannelSchema = z
  .enum(['INSTAGRAM', 'YOUTUBE', 'TWITTER', 'TIKTOK', 'NAVER_BLOG', 'THREADS', 'SNAP'])
  .describe('UpdateSeedingCampaignSnsChannelSchema');

const UpdateSeedingCampaignContentTypeSchema = z
  .enum(['IMAGE', 'SHORT_FORM', 'LONG_FORM', 'TEXT', 'BLOG_POST'])
  .describe('UpdateSeedingCampaignContentTypeSchema');

const UpdateSeedingCampaignGoodsSchema = z
  .object({
    goodsNo: z.number(),
    checkedValueIds: z.array(z.number()),
    extraValueIds: z.array(z.number().int()).optional(),
  })
  .describe('UpdateSeedingCampaignGoodsSchema');

const UpdateSeedingCampaignRequestQuerySchema = z
  .object({
    platform: z.string(),
  })
  .describe('UpdateSeedingCampaignRequestQuerySchema');

const UpdateSeedingCampaignRequestBodySchema = z
  .object({
    brandId: z.string(),
    title: z.string().max(100).nullable().optional(),
    goods: z.array(UpdateSeedingCampaignGoodsSchema).min(1),
    snsChannels: z.array(UpdateSeedingCampaignSnsChannelSchema).min(1),
    contentTypes: z.array(UpdateSeedingCampaignContentTypeSchema).min(1),
    requiredHashtags: z.array(z.string()).min(1).max(5),
    requestNote: z.string().max(500).nullable().optional(),
    maxParticipants: z.number().int().min(1).max(20).nullable().optional(),
    recruitmentStartAt: z.string().nullable().optional(),
    recruitmentEndAt: z.string().nullable().optional(),
  })
  .describe('UpdateSeedingCampaignRequestBodySchema');

const UpdateSeedingCampaignRequestSchema = z.object({
  campaignId: z.union([z.number(), z.string()]),
  query: UpdateSeedingCampaignRequestQuerySchema,
  body: UpdateSeedingCampaignRequestBodySchema,
});

export type UpdateSeedingCampaignRequest = z.infer<typeof UpdateSeedingCampaignRequestSchema>;

const UpdateSeedingCampaignResponseDataSchema = z.unknown();
const UpdateSeedingCampaignResponseSchema = createApiResponseSchema(
  UpdateSeedingCampaignResponseDataSchema,
);

export type UpdateSeedingCampaignResponse = z.infer<typeof UpdateSeedingCampaignResponseSchema>;

export const updateSeedingCampaign = withApiErrorCapture(
  lazyPipe(
    validateApiRequest(UpdateSeedingCampaignRequestSchema),
    async (request: UpdateSeedingCampaignRequest) =>
      executeStandardRequest(async () =>
        partnerHttpClient.put<UpdateSeedingCampaignResponse>(
          partnerApiPath.updateSeedingCampaign(request.campaignId),
          request.body,
          { params: request.query },
        ),
      ),
    validateApiResponse(UpdateSeedingCampaignResponseSchema),
  ),
);
